import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Award, Camera, TrendingUp, GraduationCap, Heart, Check, ChevronDown, UserSquare, Crown, UsersRound, ShieldCheck } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';

/**
 * PersonaPicker — compact dropdown that lets a signed-in user self-identify.
 *
 * Stored on the user record (field: `persona`). Used to tailor rows, CTAs,
 * and contact-form defaults downstream.
 *
 * Adding a new persona later? Append to PERSONAS and update the backend
 * whitelist in server.py update_public_profile.
 */
export const PERSONAS = [
  { id: 'athlete',   label: 'Athlete',    blurb: 'Pro, college, draft, rising.',          Icon: Award,          accent: 'bg-blue-500',    text: 'text-blue-600' },
  { id: 'agent',     label: 'Agent',      blurb: 'Manage a roster of athletes.',          Icon: UsersRound,     accent: 'bg-amber-500',   text: 'text-amber-600' },
  { id: 'recruiter', label: 'Recruiter',  blurb: 'College / pro scout building a class.', Icon: ShieldCheck,    accent: 'bg-cyan-500',    text: 'text-cyan-600' },
  // iter648 · "Brand" persona re-labeled to "Creator" (photographer,
  // videographer, editor, etc.). The id stays 'brand' to preserve all
  // existing backend integrations (portals, subscriptions, brand deals)
  // — only the surface label/blurb/icon change.
  { id: 'brand',     label: 'Creator',    blurb: 'Photographer, videographer, editor.',   Icon: Camera,         accent: 'bg-white/[0.08]', text: 'text-white' },
  { id: 'program',   label: 'Program',    blurb: 'Athletic dept, AD, coach.',             Icon: GraduationCap,  accent: 'bg-purple-500',  text: 'text-purple-600' },
  { id: 'fan',       label: 'Fan',        blurb: 'Here for the stories.',                 Icon: Heart,          accent: 'bg-rose-500',    text: 'text-rose-600' },
];

// Restricted persona — only the founder allowlist (validated server-side too)
// gets to choose it. Rendered as a separate "owner-only" row in the dropdown.
const FOUNDER_PERSONA = {
  id: 'founder',
  label: 'Founders View',
  blurb: 'See everything happening across all tabs.',
  Icon: Crown,
  accent: 'bg-yellow-300',
  text: 'text-yellow-500',
};

export const personaById = (id) => {
  if (id === FOUNDER_PERSONA.id) return FOUNDER_PERSONA;
  return PERSONAS.find((p) => p.id === id) || null;
};

/**
 * Compact dropdown shown at the bottom-right of the profile header.
 * Click to expand → list of personas → click one to save → toast confirms.
 */
const PersonaPicker = ({ compact = false }) => {
  const { user, updateProfile } = useUserAuth();
  const [open, setOpen] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  // Close on outside click + Escape, and track popover position so it
  // escapes the parent card's overflow-hidden via portal.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const popWidth = compact ? 288 : 320;
      const left = Math.max(8, Math.min(window.innerWidth - popWidth - 8, r.right - popWidth));
      setCoords({ top: r.bottom + 8 + window.scrollY, left });
    };
    place();
    const onClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, compact]);

  if (!user) return null;
  const current = user.persona || null;
  const currentMeta = personaById(current);
  const TriggerIcon = currentMeta?.Icon || UserSquare;

  const handlePick = async (id) => {
    const next = current === id ? '' : id;
    setSavingId(id);
    try {
      await updateProfile({ persona: next });
      if (next) {
        const p = personaById(next);
        toast.success(`You're set as ${p?.label}`, {
          description: "We'll tailor what you see based on this.",
          duration: 2200,
        });
      } else {
        toast('Persona cleared', { duration: 1500 });
      }
      setOpen(false);
    } catch (err) {
      // iter132 · Surface the backend's detailed error (e.g. the
      // 423 "Persona is locked for X more days" message from the
      // 30-day rule) instead of the generic "Could not save".
      // Falls back only when the server doesn't send a detail.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 423) {
        toast.error(detail || 'Your persona can only be changed once every 30 days.', {
          duration: 4200,
        });
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error('Could not save. Try again.');
      }
    } finally {
      setSavingId(null);
    }
  };

  // Persona lock: when the admin has tied this user's email to a client
  // portal with a forced_persona, we replace the dropdown with a static,
  // un-clickable badge. Stops accidental persona switches that would
  // visually drop the [BRAND] PORTAL pill (the previous bug) and keeps
  // brand-team-members anchored to the lens the admin assigned.
  if (user.persona_locked) {
    const meta = currentMeta || personaById('brand');
    const Icon = meta?.Icon || UserSquare;
    return (
      <div
        className={`group inline-flex items-center gap-${compact ? '1.5' : '2'} ${
          compact ? 'px-2.5 py-1.5 text-[11px]' : 'px-4 py-2 text-xs'
        } rounded-full border-2 font-bold uppercase tracking-wider ${
          meta ? `${meta.accent} ${meta.id === 'founder' ? 'text-black' : 'text-white'} border-transparent shadow-md` : 'bg-gray-200 text-black border-transparent'
        }`}
        title="Set by your account administrator"
        data-testid="persona-locked-badge"
      >
        <Icon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        <span className={compact ? 'max-w-[80px] truncate' : ''}>{meta?.label || 'Locked'}</span>
        {/* Tiny padlock so the visual difference vs. a normal pill is obvious. */}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
          className={compact ? 'w-3 h-3 opacity-80' : 'w-3.5 h-3.5 opacity-80'}>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative inline-block" data-testid="persona-picker">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`group inline-flex items-center gap-${compact ? '1.5' : '2'} ${compact ? 'px-2.5 py-1.5 text-[11px]' : 'px-4 py-2 text-xs'} rounded-full border-2 font-bold uppercase tracking-wider transition active:scale-[0.97] ${
          currentMeta
            ? `${currentMeta.accent} ${currentMeta.id === 'founder' ? 'text-black' : 'text-white'} border-transparent shadow-md hover:shadow-lg`
            : 'bg-white text-black border-gray-300 hover:border-black'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="persona-trigger"
      >
        <TriggerIcon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        <span className={compact ? 'max-w-[80px] truncate' : ''}>{currentMeta?.label || "I'm a..."}</span>
        <ChevronDown className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={popRef}
          style={{ position: 'absolute', top: coords.top, left: coords.left, width: compact ? 288 : 320, zIndex: 60 }}
          className="bg-white text-black rounded-2xl shadow-2xl ring-1 ring-black/10 overflow-hidden origin-top-right animate-in fade-in zoom-in-95 duration-150"
          role="listbox"
          data-testid="persona-dropdown"
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Choose your lens</p>
            <p className="text-sm font-bold leading-tight mt-0.5">Tell us who you are</p>
          </div>
          <ul className="py-1 max-h-[60vh] overflow-y-auto">
            {PERSONAS.map((p) => {
              const isActive = current === p.id;
              const isSaving = savingId === p.id;
              const Icon = p.Icon;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(p.id)}
                    disabled={isSaving}
                    className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition disabled:opacity-60 ${
                      isActive ? 'bg-gray-50' : 'hover:bg-gray-50'
                    }`}
                    aria-selected={isActive}
                    role="option"
                    data-testid={`persona-option-${p.id}`}
                  >
                    <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center text-white ${p.accent}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-bold leading-tight">{p.label}</span>
                      <span className="block text-xs text-gray-500 leading-snug">{p.blurb}</span>
                    </span>
                    {isActive && <Check className={`w-4 h-4 mt-2 ${p.text}`} />}
                  </button>
                </li>
              );
            })}
            {/* Founders View — appears for both founders and executives.
                For executives the row reads "Executive View" in blue. */}
            {user.is_founder && (() => {
              const isExec = user.role === 'executive';
              const p = {
                ...FOUNDER_PERSONA,
                label: isExec ? 'Executive View' : FOUNDER_PERSONA.label,
                blurb: isExec
                  ? 'See everything happening across all tabs.'
                  : FOUNDER_PERSONA.blurb,
                accent: isExec ? 'bg-sky-500' : FOUNDER_PERSONA.accent,
                text: isExec ? 'text-sky-600' : FOUNDER_PERSONA.text,
              };
              const Icon = isExec ? Check : FOUNDER_PERSONA.Icon;
              const isActive = current === p.id;
              const isSaving = savingId === p.id;
              const hoverCls = isExec ? 'hover:bg-sky-50/60' : 'hover:bg-yellow-50/60';
              const activeCls = isExec ? 'bg-sky-50' : 'bg-yellow-50';
              const tagCls = isExec
                ? 'text-sky-700 bg-sky-100'
                : 'text-yellow-600 bg-yellow-100';
              const tagText = isExec ? 'Executive' : 'Owner';
              return (
                <li key={p.id} className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => handlePick(p.id)}
                    disabled={isSaving}
                    className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition disabled:opacity-60 ${
                      isActive ? activeCls : hoverCls
                    }`}
                    aria-selected={isActive}
                    role="option"
                    data-testid={`persona-option-${p.id}`}
                  >
                    <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${isExec ? 'text-white' : 'text-black'} ${p.accent}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="block text-sm font-bold leading-tight">{p.label}</span>
                        <span className={`text-[9px] font-bold uppercase tracking-widest rounded-full px-1.5 py-0.5 ${tagCls}`}>{tagText}</span>
                      </span>
                      <span className="block text-xs text-gray-500 leading-snug">{p.blurb}</span>
                    </span>
                    {isActive && <Check className={`w-4 h-4 mt-2 ${p.text}`} />}
                  </button>
                </li>
              );
            })()}
          </ul>
          {current && (
            <button
              type="button"
              onClick={() => handlePick(current)}
              className="w-full text-center text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-black py-2.5 border-t border-gray-100"
              data-testid="persona-clear"
            >
              Clear selection
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

/**
 * Small pill badge that shows the user's current persona next to their name.
 * Renders nothing if no persona is selected.
 */
export const PersonaBadge = ({ personaId, className = '' }) => {
  const p = personaById(personaId);
  if (!p) return null;
  const Icon = p.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${p.id === 'founder' ? 'text-black' : 'text-white'} ${p.accent} ${className}`}
      data-testid={`persona-badge-${p.id}`}
    >
      <Icon className="w-3 h-3" />
      {p.label}
    </span>
  );
};

export default PersonaPicker;
